import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled, { type CSSProperties } from 'styled-components';

import { GlareEffect } from '@sorare/core/src/atoms/animations/GlareEffect';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, BrandXS } from '@sorare/core/src/atoms/typography';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { CornerBadge, Position } from '@sorare/core/src/components/CornerBadge';
import { ANY_SPORT_MARKET_COMMON_PACKS_GROUP } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { glossary } from '@sorare/core/src/lib/glossary';

import type { DailyDealsTile_cardPackGroup } from './__generated__/index.graphql';

const Root = styled(ButtonBase)`
  position: relative;
  isolation: isolate;
  padding: var(--quadruple-unit) var(--triple-unit) 0;
  background:
    linear-gradient(to bottom, transparent 77%, var(--c-black) 100%),
    var(--c-background-image) bottom center/100% 100% no-repeat;
  border-radius: var(--double-unit);
`;

const PositionBadge = styled(Horizontal).attrs({ center: true })`
  position: absolute;
  top: var(--unit);
  right: 0;
`;

const PacksWrapper = styled(Horizontal)`
  padding: var(--triple-unit) var(--double-unit) 0;
  max-height: calc(12 * var(--unit));
  overflow: hidden;
  > *:first-child {
    transform: rotate(-15deg) scale(0.85);
    margin-right: -48px;
  }
  > *:last-child {
    z-index: -1;
    margin-left: -48px;
    transform: rotate(15deg) scale(0.85);
  }
`;

const PackImg = styled(GlareEffect)`
  object-fit: contain;
  height: 100%;
  width: calc(10 * var(--unit));
`;

type Props = {
  bgImage: string;
  cardPackGroup: DailyDealsTile_cardPackGroup;
};
export const DailyDealsTile = ({ bgImage, cardPackGroup }: Props) => {
  const { cardPacks, id } = cardPackGroup;
  const { generateSportContextPath } = useSportContext();

  return (
    <Root
      style={
        {
          '--c-background-image': `url(${bgImage})`,
        } as CSSProperties
      }
      to={`${generateSportContextPath(ANY_SPORT_MARKET_COMMON_PACKS_GROUP, {
        params: {
          group: idFromObject(id),
        },
      })}?pack=${cardPacks?.[0]?.slug}`}
    >
      <PositionBadge>
        <CornerBadge align={Position.RIGHT} color="var(--c-yellow-400)">
          <BrandXS>
            <FormattedMessage {...glossary.dailyDeals} />
          </BrandXS>
        </CornerBadge>
      </PositionBadge>
      <Vertical gap={3} center>
        <BrandM>
          <FormattedMessage {...glossary.dailyDeals} />
        </BrandM>
        <PacksWrapper>
          {cardPacks.map(pack => {
            return (
              <PackImg
                key={pack.slug}
                pictureUrl={proxyUrl(pack.pictureUrl, {
                  cropWidth: 160,
                })}
              />
            );
          })}
        </PacksWrapper>
      </Vertical>
    </Root>
  );
};

DailyDealsTile.fragments = {
  cardPackGroup: gql`
    fragment DailyDealsTile_cardPackGroup on CardPackGroup {
      id
      cardPacks {
        slug
        pictureUrl
      }
    }
  ` as TypedDocumentNode<DailyDealsTile_cardPackGroup>,
};
